import { useState, useEffect } from 'react';
import { notificationsAPI } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { getLocalizedErrorMessage } from '@/lib/error-i18n';
import i18n from '@/lib/i18n';

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const { toast } = useToast();

  // 🔹 Fetch unread notifications
  const fetchNotifications = async () => {
    try {
      setIsLoading(true);

      const data = await notificationsAPI.getNotifications();
      setNotifications(data || []);
      setUnreadCount(data?.length || 0);
    } catch (error: any) {
      console.error('Error fetching notifications:', error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // 🔹 Fetch unread count
  const fetchUnreadCount = async () => {
    try {
      const data = await notificationsAPI.getUnreadCount();
      setUnreadCount(data.unreadCount || 0);
    } catch (error: any) {
      console.error('Error fetching unread count:', error.message);
    }
  };

  // 🔹 Mark all as read
  const markAllAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();

      setUnreadCount(0);
      setNotifications([]);

      toast({
        title: "Notifications",
        description: "All notifications marked as read",
      });
    } catch (error: any) {
      toast({
        title: i18n.t("common.error"),
        description: getLocalizedErrorMessage(error, "errors.generic"),
        variant: "destructive",
      });
    }
  };

  // 🔹 Handle real-time new notification
  const addNotification = (notification: any) => {
    setNotifications(prev => {
      const exists = prev.some(n => n._id === notification._id);
      if (exists) return prev;

      return [notification, ...prev];
    });

    setUnreadCount(prev => prev + 1);
  };

  // Initial load
  useEffect(() => {
    fetchUnreadCount();

    const handler = (event: any) => {
      const notif = event.detail;
      addNotification(notif);
    };

    const handleFocus = () => {
      fetchUnreadCount();
    };

    window.addEventListener('notification:new', handler);
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('notification:new', handler);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  return {
    notifications,
    unreadCount,
    isLoading,
    fetchNotifications,
    fetchUnreadCount,
    markAllAsRead,
    addNotification
  };
};
